(ns manage.ad-variant.creation.template.add-modal
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            ["src/svg/type/thumbnails/meta-single-image.svg$default" :as MetaSingleImage]
            [clojure.set :as set]
            [common.modal.view :refer [defmodal modal-v2]]
            [common.queries :refer [use-query]]
            [common.resource.query.adset :as adset.q]
            [common.widgets.form-field :refer [form-field hint]]
            [manage.ad-variant.creation.product-strategy.core :as product-strategy.core]
            [manage.ad-variant.creation.template.card-group-item :as cgi]
            [manage.ad-variant.creation.template.hooks :as h]
            [manage.ad-variant.data :as d]
            [manage.ad-variant.queries :as q]
            [manage.adset-template-page.view.templates-selection :as ts]
            [manage.adset.hooks :refer [use-adset]]
            [manage.template-table.db :refer [template-table-ns]]
            [re-frame.core :as rf]
            [uix.core :refer [$ defui] :as uix]
            [uix.re-frame :refer [use-subscribe]]))

(def num-of-steps 2)

(def template-selection-step 1)
(def format-selection-step 2)

(def default-card-opts
  {:thumbnail MetaSingleImage
   :title ""
   :description ""
   :disabled false
   :value ""
   :selected-value ""
   :on-click #(rf/dispatch [:ad-variant-creation/update-ad-types-selection (first %) (second %)])})

(defui ad-types-selector
  [{:keys [assigned adset]}]
  (let [ad-types (use-subscribe [:ad-variant-creation/ad-types])
        opts (merge default-card-opts {:selected-values ad-types})]
    ($ Stack {:direction "row" :sx #js {:gap 2}}
       (for [[value cfg] (d/dynamic-creation-ad-types adset)]
         (cgi/card-group-item
          (merge opts cfg {:key value
                           :value value
                           :disabled (contains? assigned value)}))))))

(defui template-select
  [{:keys [assigned-templates->ad-types adset]}]
  (let [channel (:channel adset)
        ns (template-table-ns channel)
        qualify (uix/use-callback (fn [e]
                                    (keyword ns e))
                                  [ns])]
    (uix/use-effect (fn []
                      (rf/dispatch [(qualify :init)]))
                    [qualify])
    ($ :<>
       ($ ts/audit-alert
          {:channel channel})
       ($ ts/filter-input
          {:qualify qualify})
       ($ ts/templates-selection-table
          {:qualify qualify
           :channel channel
           :multiselect-enabled? (not (h/use-select-ad-formats?))
           :select-fn #(rf/dispatch [:ad-variant-creation/set-templates %])
           :is-row-selectable (fn [^js params]
                                (let [template (.-row params)
                                      audit-state (-> template :latestRevision :auditState)
                                      template-dimensions ((juxt :width :height) (:latestRevision template))]
                                  ;; A template is selectable if..
                                  ;; its audit has passed, or it doesn't need to be audited
                                  (and (contains? #{nil "passed"} audit-state)
                                       ;; AND it has a template id
                                       (:id template)
                                       ;; AND it has defined dimensions
                                       (every? nat-int? template-dimensions)
                                       ;; AND it has been assigned already to some but not all available ad-types
                                       (or (let [available-ad-types (-> (d/dynamic-creation-ad-types adset) keys set)
                                                 assigned-ad-types (get assigned-templates->ad-types (:id template))
                                                 intersection (set/intersection assigned-ad-types available-ad-types)]
                                             (< 0 (count intersection) (count available-ad-types)))

                                           ;; OR has not been assigned at all
                                           (not (contains? assigned-templates->ad-types (:id template)))))))}))))

(def urn->ad-type
  (->> d/ad-types
       (keep (fn [[ad-type {:keys [ad-builder-urn]}]]
               (when ad-builder-urn
                 [ad-builder-urn ad-type])))
       (into {})))

(defui modal-content
  [{:keys [step setStep]}]
  (let [{:keys [adset-id]} (use-subscribe [:url-context])
        {upgrade-hints :data} (use-query (q/ad-builders-upgrade-hints-query {:adset-id adset-id}))

        adset (use-adset)

        first-step? (= step template-selection-step)
        template (use-subscribe [:ad-variant-creation/template])

        assigned-templates->ad-types (->> upgrade-hints
                                          (reduce (fn [acc {:keys [active ad-formats urn]}]
                                                    (if active
                                                      (let [ad-type (get urn->ad-type urn)
                                                            template-ids (mapv (fn [ad-format]
                                                                                 (get-in ad-format [:current-revision :template-id]))
                                                                               ad-formats)]
                                                        (reduce (fn [acc template-id]
                                                                  (assoc acc template-id (set/union (get acc template-id) #{ad-type})))
                                                                acc template-ids))
                                                      acc))
                                                  {}))]
    ($ Stack {:spacing 2
              :sx #js {:minWidth 720
                       :minHeight 300
                       :overflowY "hidden"}}
       (if first-step?
         ($ template-select
            {:assigned-templates->ad-types assigned-templates->ad-types
             :adset adset})
         ($ :<>
            ($ Stack {:direction "row"
                      :alignItems "flex-start"
                      :justifyContent "space-between"}
               ($ form-field
                  {:label "Selected Template"}
                  ($ Typography (:name template)))
               ($ Button {:variant "text"
                          :on-click #(setStep template-selection-step)}
                  "Change Template"))
            ($ form-field
               {:label "Format"}
               ($ ad-types-selector
                  {:assigned (get assigned-templates->ad-types (:id template))
                   :adset adset})
               ($ hint "Choose how you like to structure your ad.")))))))

(defui action-bar
  [{:keys [step setStep]}]
  (let [{:keys [adset-id]} (use-subscribe [:url-context])
        {adset :data} (use-query (adset.q/adset-query {:adset-id adset-id}))
        {ad-builders :data} (use-query (q/ad-builders-upgrade-hints-query {:adset-id adset-id}))
        product-strategy-id (uix/use-memo (fn []
                                            (some (fn [{:keys [ad-formats]}]
                                                    (some product-strategy.core/ad-format->product-strategy-id ad-formats))
                                                  ad-builders))
                                          [ad-builders])
        final-step? (= step num-of-steps)
        should-select-ad-formats? (h/use-select-ad-formats?)
        final-step? (or (not should-select-ad-formats?) final-step?)
        ad-types (use-subscribe [:ad-variant-creation/ad-types])
        template (use-subscribe [:ad-variant-creation/template])
        button-label (if final-step? "Done" "Continue")
        button-enabled? (if final-step?
                          (if should-select-ad-formats? (seq ad-types) (some? template))
                          (some? template))]
    ($ Stack {:direction "row" :spacing 1}
       ($ Button {:variant "link"
                  :on-click #(rf/dispatch [:modal/close])}
          "Cancel")
       ($ Button {:variant "contained"
                  :on-click #(if final-step?
                               (rf/dispatch [:ad-variant-creation/create-ad-builder product-strategy-id (:channel adset)])
                               (setStep (inc step)))
                  :disabled (not button-enabled?)}
          button-label))))

(defmodal add-template :ad-variant/add-template-modal
  []
  (let [[step set-step] (uix/use-state 1)
        opts {:step step :setStep set-step}]
    (uix/use-effect (fn []
                      (fn []
                        (rf/dispatch [:ad-variant-creation/clear])))
                    [])
    ($ modal-v2
       {:title "Add Templates"
        :footer ($ action-bar opts)
        :dialog-content-props {:sx #js {:display "flex"
                                        :overflowY "hidden"}}}
       ($ modal-content opts))))
